import { useTranslation } from "react-i18next";
import { IDashboardStats } from "../../models/DashboardStats";
import DashboardTile from "./DashboardTile";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

interface IVehicleProcessingTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type DashboardStatsExtended = IDashboardStats & {
  vehiclesAwaitingDepollution?: number;
  vehiclesAwaitingDepollutionToday?: number;
  vehiclesAwaitingTriage?: number;
  vehiclesAwaitingTriageToday?: number;
  vehiclesAwaitingDismantling?: number;
  vehiclesAwaitingDismantlingToday?: number;
  vehiclesAwaitingCrushing?: number;
  vehiclesAwaitingCrushingToday?: number;
};

type ProcessingRow = {
  key: keyof DashboardStatsExtended;
  todayKey: keyof DashboardStatsExtended;
  label: string;
  bgClass: string;
  textClass: string;
};

const depollutionRow: ProcessingRow = { key: "vehiclesAwaitingDepollution", todayKey: "vehiclesAwaitingDepollutionToday", label: "Awaiting Depollution", bgClass: "bg-blue-50", textClass: "text-blue-700" };
const triageRow: ProcessingRow = { key: "vehiclesAwaitingTriage", todayKey: "vehiclesAwaitingTriageToday", label: "Awaiting Triage", bgClass: "bg-emerald-50", textClass: "text-emerald-700" };

const rowConfig: ProcessingRow[] = [
  depollutionRow,
  { key: "vehiclesAwaitingDismantling", todayKey: "vehiclesAwaitingDismantlingToday", label: "Awaiting Dismantling", bgClass: "bg-violet-50", textClass: "text-violet-700" },
  { key: "vehiclesAwaitingCrushing", todayKey: "vehiclesAwaitingCrushingToday", label: "Awaiting Crushing", bgClass: "bg-rose-50", textClass: "text-rose-700" },
];

function formatValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

export default function VehicleProcessingTable({ dashboardStats, span = 2 }: IVehicleProcessingTableProps) {
  const { t } = useTranslation("dashboard");

  if (!dashboardStats) return null;

  const stats = dashboardStats as DashboardStatsExtended;
  const hasTriage = CheckFeatureFlag("Triage");

  // When Triage is enabled, surface it alongside Depollution.
  const rows: ProcessingRow[] = hasTriage
    ? [depollutionRow, triageRow, ...rowConfig.slice(1)]
    : rowConfig;

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">{t("Vehicle Processing")}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{t("Vehicles awaiting processing vs processed today")}</p>
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">{t("Metric")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Awaiting Processing")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Processed Today")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Per Hour")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, todayKey, label, bgClass, textClass }) => {
                const displayLabel = label;
                const value = stats[key] as number | null | undefined;
                const todayValue = stats[todayKey] as number | null | undefined;
                const perHour =
                  typeof todayValue === "number" && !Number.isNaN(todayValue)
                    ? todayValue / 9
                    : null;
                return (
                  <tr key={String(key)} className={`font-medium transition-colors ${bgClass}`}>
                    <td className={`py-2.5 pl-4 pr-4 ${textClass}`}>{t(displayLabel)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(value)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(todayValue)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>
                      {perHour != null ? perHour.toFixed(1) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
